import React, { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useAppStore from '../store/useAppStore';
import { questions } from '../data/questions';
import { levels } from '../data/levels';
import { Stage, Module } from '../types';
import styles from './Challenge.module.css';

const moduleLabels: Record<string, string> = {
  [Module.FEEDING]: '喂养',
  [Module.SLEEP]: '睡眠',
  [Module.DAILY_CARE]: '护理',
  [Module.SAFETY]: '安全',
  [Module.DEVELOPMENT]: '发育',
};

const StageChallenge: React.FC = () => {
  const navigate = useNavigate();
  const { stageId } = useParams<{ stageId: string }>();
  const startStageChallenge = useAppStore((s) => s.startStageChallenge);
  const completeStageChallenge = useAppStore((s) => s.completeStageChallenge);

  // 解析 stage
  const stage = useMemo(() => {
    const entry = Object.entries(Stage).find(([, v]) => v === stageId || v === decodeURIComponent(stageId || ''));
    return entry ? (entry[1] as Stage) : Stage.NEWBORN;
  }, [stageId]);

  // 抽取题目（仅执行一次）
  const challengeData = useMemo(() => {
    return startStageChallenge(stage, questions, levels);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { questionIds } = challengeData;

  const challengeQuestions = useMemo(
    () => questionIds.map((id) => questions.find((q) => q.id === id)).filter(Boolean) as typeof questions,
    [questionIds]
  );

  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string | string[]>>({});
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [showExplanation, setShowExplanation] = useState(false);
  const [finished, setFinished] = useState(false);
  const [resultData, setResultData] = useState<{
    correctCount: number;
    total: number;
    bonusTriggered: boolean;
    isFirstWin: boolean;
    streak: number;
    reachedMilestone: number | null;
  } | null>(null);

  // 查找题目所属模块
  const getQuestionModule = (q: typeof questions[0]): string => {
    const level = levels.find((l) => l.id === q.levelId);
    return level ? moduleLabels[level.module] || level.module : '未知';
  };

  const currentQ = challengeQuestions[currentIndex];
  const isLast = currentIndex === challengeQuestions.length - 1;
  const isAnswered = !!answers[currentQ?.id];

  const handleSingleAnswer = (option: string) => {
    if (isAnswered) return;
    setAnswers({ ...answers, [currentQ.id]: option });
    setShowExplanation(false);
  };

  const handleMultiToggle = (option: string) => {
    const current = selectedOptions.includes(option)
      ? selectedOptions.filter((o) => o !== option)
      : [...selectedOptions, option];
    setSelectedOptions(current);
  };

  const handleMultiConfirm = () => {
    if (selectedOptions.length === 0) return;
    setAnswers({ ...answers, [currentQ.id]: selectedOptions });
    setShowExplanation(false);
  };

  const checkCorrect = (qId: string) => {
    const q = questions.find((x) => x.id === qId);
    if (!q) return false;
    const userAnswer = answers[qId];
    const correctAnswer = q.correctAnswer;
    if (Array.isArray(correctAnswer) && Array.isArray(userAnswer)) {
      return (
        correctAnswer.length === userAnswer.length &&
        correctAnswer.every((a) => userAnswer.includes(a))
      );
    }
    return userAnswer === correctAnswer;
  };

  const handleNext = () => {
    if (isLast) {
      handleFinish();
    } else {
      setCurrentIndex(currentIndex + 1);
      setSelectedOptions([]);
      setShowExplanation(false);
    }
  };

  const handleFinish = () => {
    let correctCount = 0;
    questionIds.forEach((id) => {
      if (checkCorrect(id)) correctCount++;
    });

    // 签到更新（更新日学习记录和连续天数）
    const checkIn = useAppStore.getState().checkInToday();

    const result = completeStageChallenge(correctCount, questionIds.length, stage);

    // 读取签到和连胜状态
    const storeState = useAppStore.getState();
    const streak = storeState.streak.currentStreak;
    const today = new Date().toISOString().slice(0, 10);
    const isFirstWin = storeState.dailyFirstWinDate === today;
    // 检测本次是否恰巧触发了里程碑
    const reachedMilestone = checkIn.reachedMilestone;

    setResultData({
      correctCount,
      total: questionIds.length,
      bonusTriggered: result.bonusTriggered,
      isFirstWin,
      streak,
      reachedMilestone,
    });
    setFinished(true);
  };

  // 空状态：还没完成任何关卡
  if (challengeQuestions.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyCard}>
          <div className={styles.emptyIcon}>🗺️</div>
          <h2 className={styles.emptyTitle}>暂无可用题目</h2>
          <p className={styles.emptySub}>
            需要先完成至少一个关卡，才能解锁阶段混合闯关模式。
          </p>
          <button className={styles.backBtn} onClick={() => navigate('/map')}>
            返回学习地图
          </button>
        </div>
      </div>
    );
  }

  // 完成结果
  if (finished && resultData) {
    const { correctCount, total, bonusTriggered, isFirstWin, streak, reachedMilestone } = resultData;
    const allPassed = correctCount === total;
    const rate = Math.round((correctCount / total) * 100);
    const missedCount = total - correctCount;

    // 按模块统计正确率
    const moduleStats: Record<string, { total: number; correct: number }> = {};
    questionIds.forEach((id) => {
      const q = questions.find((x) => x.id === id);
      if (!q) return;
      const mod = getQuestionModule(q);
      if (!moduleStats[mod]) moduleStats[mod] = { total: 0, correct: 0 };
      moduleStats[mod].total++;
      if (checkCorrect(id)) moduleStats[mod].correct++;
    });

    const getResultEmoji = (r: number) => {
      if (r >= 90) return '🌟';
      if (r >= 70) return '👍';
      if (r >= 50) return '💪';
      return '📚';
    };

    // 「就差一点」个性化提示
    const getJustOneMoreTip = () => {
      if (rate === 100) return { emoji: '🏆', text: '完美通关！继续保持这个水准！' };
      if (rate >= 80) return { emoji: '🔥', text: `就差 ${missedCount} 题满分，再试一次？` };
      if (rate >= 60) return { emoji: '💪', text: '再来一次你肯定能全对！' };
      if (rate >= 40) return { emoji: '📚', text: '多练几次就能明显进步！' };
      return { emoji: '🎯', text: '基础需要巩固，建议先去复习错题' };
    };
    const tip = getJustOneMoreTip();

    return (
      <div className={styles.container}>
        <div className={`${styles.resultCard} ${allPassed ? styles.resultPass : styles.resultFail}`}>
          {/* 暴击动画覆盖层 */}
          {bonusTriggered && (
            <div className={styles.bonusOverlay}>
              <div className={styles.bonusSparkle}>✨</div>
              <div className={styles.bonusText}>
                <span className={styles.bonusLabel}>🎰 幸运暴击！</span>
                <span className={styles.bonusPlus}>+1 ⭐</span>
              </div>
            </div>
          )}

          <div className={styles.resultIcon}>{allPassed ? '🎉' : '💪'}</div>
          <h2 className={styles.resultTitle}>
            {allPassed ? '恭喜通关！' : '继续加油'}
          </h2>
          <div className={styles.scoreRow}>
            <span className={styles.scoreNum}>{correctCount}</span>
            <span className={styles.scoreSep}>/</span>
            <span className={styles.scoreTotal}>{total}</span>
          </div>
          <p className={styles.resultHint}>
            {allPassed
              ? `出色！你在阶段闯关中全部答对了！`
              : `正确率 ${rate}%`}
          </p>

          {/* ===== 层次一：趣味性反馈 ===== */}

          {/* 暴击提示（非覆盖层替代） */}
          {bonusTriggered && (
            <div className={styles.bonusBanner}>
              <span className={styles.bonusBannerIcon}>🎰</span>
              <span>幸运暴击触发！额外获得 <strong>+1 ⭐</strong></span>
            </div>
          )}

          {/* 今日首胜 */}
          {isFirstWin && (
            <div className={styles.firstWinBanner}>
              <span className={styles.firstWinIcon}>🎯</span>
              <span>今日首胜达成！</span>
            </div>
          )}

          {/* 连胜信息 */}
          <div className={styles.streakInfo}>
            <span className={styles.streakIcon}>🔥</span>
            <span>连续学习 <strong>{streak}</strong> 天</span>
            {reachedMilestone && (
              <span className={styles.milestoneBadge}>
                🏆 {reachedMilestone} 天里程碑！
              </span>
            )}
          </div>

          {/* 「就差一点」提示 */}
          {rate < 100 && (
            <div className={styles.justOneMore}>
              <span className={styles.justOneMoreEmoji}>{tip.emoji}</span>
              <span>{tip.text}</span>
            </div>
          )}

          {/* 各模块得分明细 */}
          {Object.keys(moduleStats).length > 1 && (
            <div className={styles.moduleSection}>
              <p className={styles.moduleTitle}>📊 各模块表现</p>
              {Object.entries(moduleStats).map(([mod, stats]) => {
                const modRate = Math.round((stats.correct / stats.total) * 100);
                return (
                  <div key={mod} className={styles.moduleRow}>
                    <span>{getResultEmoji(modRate)} {mod}</span>
                    <span className={modRate >= 80 ? styles.moduleGood : modRate >= 50 ? styles.moduleMid : styles.moduleLow}>
                      {stats.correct}/{stats.total} ({modRate}%)
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          <div className={styles.resultActions}>
            <button className={styles.retryBtn} onClick={() => {
              startStageChallenge(stage, questions, levels);
              window.location.reload();
            }}>
              再来一次
            </button>
            <button className={styles.backReviewBtn} onClick={() => navigate('/map')}>
              返回学习地图
            </button>
            {correctCount < total && (
              <button className={styles.reviewWrongBtn} onClick={() => navigate(`/review/${stage}`)}>
                复习错题
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const progressPercent = challengeQuestions.length > 0
    ? Math.round(((currentIndex + 1) / challengeQuestions.length) * 100)
    : 0;

  const currentModule = currentQ ? getQuestionModule(currentQ) : '';

  return (
    <div className={styles.container}>
      <div className={styles.topBar}>
        <button className={styles.homeLink} onClick={() => navigate('/map')}>
          ← 退出
        </button>
        <span className={styles.modeLabel}>🎯 阶段混合闯关</span>
        {currentModule && (
          <span style={{
            fontSize: '12px',
            color: '#8b5e3c',
            background: '#fefaf6',
            padding: '3px 10px',
            borderRadius: '8px',
            border: '1px solid #f0ddd0',
          }}>
            {currentModule}
          </span>
        )}
      </div>
      <div className={styles.progressBar}>
        <div
          className={styles.progressFill}
          style={{ width: `${progressPercent}%` }}
        />
      </div>
      <div className={styles.progressText}>
        题目 {currentIndex + 1} / {challengeQuestions.length}
      </div>

      {currentQ && (
        <div className={styles.questionCard} key={currentIndex}>
          <div className={styles.questionType}>
            {currentQ.type === 'single' && '单选题'}
            {currentQ.type === 'multiple' && '多选题'}
            {currentQ.type === 'truefalse' && '判断题'}
            {currentQ.type === 'scenario' && '情景题'}
          </div>

          <h2 className={styles.questionText}>{currentQ.text}</h2>

          <div className={styles.options}>
            {currentQ.options?.map((option, idx) => {
              const userAnswer = answers[currentQ.id];
              const correctAnswer = currentQ.correctAnswer;

              if (currentQ.type === 'multiple') {
                const isSelected = selectedOptions.includes(option);
                let optionClass = styles.optionBtn;
                if (isAnswered) {
                  const isCorrectOption = Array.isArray(correctAnswer) && correctAnswer.includes(option);
                  if (isCorrectOption) optionClass += ` ${styles.optionCorrect}`;
                  else if (isSelected) optionClass += ` ${styles.optionWrong}`;
                  else optionClass += ` ${styles.optionDisabled}`;
                } else if (isSelected) {
                  optionClass += ` ${styles.optionSelected}`;
                }
                return (
                  <button
                    key={idx}
                    className={optionClass}
                    onClick={() => handleMultiToggle(option)}
                    disabled={isAnswered}
                  >
                    <span style={{ marginRight: 6, fontWeight: 700 }}>
                      {isSelected ? '☑' : '☐'}
                    </span>
                    {option}
                    {isAnswered && Array.isArray(correctAnswer) && correctAnswer.includes(option) && (
                      <span className={styles.optionMark}>✓</span>
                    )}
                    {isAnswered && isSelected && Array.isArray(correctAnswer) && !correctAnswer.includes(option) && (
                      <span className={styles.optionMark}>✗</span>
                    )}
                  </button>
                );
              }

              const isSelected = userAnswer === option;
              const isCorrectOption = correctAnswer === option;
              let optionClass = styles.optionBtn;
              if (isAnswered) {
                if (isCorrectOption) optionClass += ` ${styles.optionCorrect}`;
                else if (isSelected) optionClass += ` ${styles.optionWrong}`;
                else optionClass += ` ${styles.optionDisabled}`;
              } else if (isSelected) {
                optionClass += ` ${styles.optionSelected}`;
              }
              return (
                <button
                  key={idx}
                  className={optionClass}
                  onClick={() => handleSingleAnswer(option)}
                  disabled={isAnswered}
                >
                  <span className={styles.optionIdx}>{String.fromCharCode(65 + idx)}.</span>
                  {option}
                  {isAnswered && isCorrectOption && <span className={styles.optionMark}>✓</span>}
                  {isAnswered && isSelected && !isCorrectOption && <span className={styles.optionMark}>✗</span>}
                </button>
              );
            })}
          </div>

          {/* 多选题确认按钮 */}
          {currentQ.type === 'multiple' && !isAnswered && selectedOptions.length > 0 && (
            <button className={styles.submitBtn} onClick={handleMultiConfirm}>
              确认选择
            </button>
          )}

          {isAnswered && !showExplanation && (
            <div className={`${styles.feedback} ${checkCorrect(currentQ.id) ? styles.correct : styles.wrong}`}>
              <span className={styles.feedbackIcon}>
                {checkCorrect(currentQ.id) ? '✓' : '✗'}
              </span>
              <span>{checkCorrect(currentQ.id) ? '回答正确！' : '回答错误'}</span>
              <button className={styles.showExplainBtn} onClick={() => setShowExplanation(true)}>
                查看解释
              </button>
            </div>
          )}

          {showExplanation && (
            <div className={styles.explanation}>
              <h3>📖 解释</h3>
              <p>{currentQ.explanation}</p>
            </div>
          )}

          {isAnswered && (
            <button className={styles.submitBtn} onClick={handleNext}>
              {isLast ? '查看结果' : '下一题'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default StageChallenge;
